import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  createDependent,
  deleteDependent,
  updateDependent,
} from "../../../../../../api/filings/filingAPI";
import { Handler401 } from "../../../../../../utils/auth/authHandler";
import { transformDependentFromAPI, transformDependentToAPI } from "../helpers/dependents";

export function useDependentActions(
  filingUuid,
  dependents,
  setDependents,
  setFiling,
) {
  const navigate = useNavigate();
  const [editingDependentIndex, setEditingDependentIndex] = useState(null);
  const [deleteDependentIndex, setDeleteDependentIndex] = useState(null);

  const handleSubmitDependent = async (dependentData) => {
    if (!filingUuid) return;
    const apiData = transformDependentToAPI(dependentData);

    try {
      let response;
      if (editingDependentIndex !== null) {
        const dependentToUpdate = dependents[editingDependentIndex];
        const response = await updateDependent(
          filingUuid,
          dependentToUpdate.id,
          apiData,
          { includeMetrics: true },
        );

        if (response.ok) {
          const responseData = await response.json();
          const { summary_metrics, ...updatedDependent } = responseData;
          const transformedDependent =
            transformDependentFromAPI(updatedDependent);
          const newDependents = [...dependents];
          newDependents[editingDependentIndex] = transformedDependent;
          setDependents(newDependents);
          if (summary_metrics)
            setFiling((prev) => ({ ...prev, summary_metrics }));
          return true; // Used to close modal
        }
      } else {
        const response = await createDependent(filingUuid, apiData, {
          includeMetrics: true,
        });
        if (response.ok) {
          const responseData = await response.json();
          const { summary_metrics, ...newDependent } = responseData;
          setDependents([
            ...dependents,
            transformDependentFromAPI(newDependent),
          ]);
          if (summary_metrics)
            setFiling((prev) => ({ ...prev, summary_metrics }));
          return true;
        }
      }
      if (response?.status === 401) Handler401(navigate);
    } catch (err) {
      console.error("Error saving dependent:", err);
      alert("Failed to save dependent. Please try again.");
    }
    return false;
  };

  const handleConfirmDeleteDependent = async () => {
    if (deleteDependentIndex === null || !filingUuid) return false;
    const dependentToDelete = dependents[deleteDependentIndex];

    try {
      const response = await deleteDependent(filingUuid, dependentToDelete.id, {
        includeMetrics: true,
      });

      if (response.ok) {
        const data = await response.json();
        setDependents(
          dependents.filter((_, idx) => idx !== deleteDependentIndex),
        );
        if (data.summary_metrics) {
          setFiling((prev) => ({
            ...prev,
            summary_metrics: data.summary_metrics,
          }));
        }
        return true;
      } else if (response.status === 401) {
        Handler401(navigate);
      }
    } catch (err) {
      console.error("Error deleting dependent:", err);
      alert("Failed to delete dependent. Please try again.");
    }
    return false;
  };

  return {
    editingDependentIndex,
    setEditingDependentIndex,
    deleteDependentIndex,
    setDeleteDependentIndex,
    handleSubmitDependent,
    handleConfirmDeleteDependent,
  };
}
